import { seo } from '#utils/seo';

const SITE_URL = 'https://teamplay.gg';

type StaticPageHeadOptions = {
  title: string;
  description: string;
  pathname: string;
  locale?: string;
  withRuLocale?: boolean;
  keywords?: string;
  /** og:image / twitter:image; defaults to the site social banner so shares always have one */
  image?: string;
  /** schema.org JSON-LD objects to emit as <script type="application/ld+json"> */
  jsonLd?: object[];
  /** Emit robots noindex (private pages like the bot admin dashboard) */
  noindex?: boolean;
};

const ensureLeadingSlash = (pathname: string) =>
  pathname.startsWith('/') ? pathname : `/${pathname}`;

const getLocalePath = (pathname: string, locale: 'en' | 'ru') =>
  locale === 'ru' ? `/ru${pathname}` : pathname;

export const staticPageHead = ({
  title,
  description,
  pathname,
  locale,
  withRuLocale = false,
  keywords,
  image,
  jsonLd,
  noindex = false,
}: StaticPageHeadOptions) => {
  const normalizedPath = ensureLeadingSlash(pathname);
  const normalizedLocale = withRuLocale && locale === 'ru' ? 'ru' : 'en';
  const canonicalPath = getLocalePath(normalizedPath, normalizedLocale);
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;

  const links = [
    {
      rel: 'canonical',
      href: canonicalUrl,
    },
    ...(withRuLocale
      ? [
          {
            rel: 'alternate',
            hrefLang: 'en',
            href: `${SITE_URL}${getLocalePath(normalizedPath, 'en')}`,
          },
          {
            rel: 'alternate',
            hrefLang: 'ru',
            href: `${SITE_URL}${getLocalePath(normalizedPath, 'ru')}`,
          },
          {
            rel: 'alternate',
            hrefLang: 'x-default',
            href: `${SITE_URL}${getLocalePath(normalizedPath, 'en')}`,
          },
        ]
      : []),
  ];

  return {
    meta: [
      ...seo({
        title,
        description,
        keywords,
        image: image ?? `${SITE_URL}/preview.png`,
        url: canonicalUrl,
      }),
      ...(noindex ? [{ name: 'robots', content: 'noindex, nofollow' }] : []),
    ],
    links,
    ...(jsonLd && jsonLd.length > 0
      ? {
          scripts: jsonLd.map((schema) => ({
            type: 'application/ld+json',
            children: JSON.stringify(schema),
          })),
        }
      : {}),
  };
};
